import { Edit } from '@ui/components/icons';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import type { DecreaseOrderRow } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/types';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';

import { EditTpSlType, GenericTradeParams } from '../types';
import { EditTpSlOverlay } from './EditTpSl/EditTpSlOverlay';
import { MergedTpSlOrdersOverlay } from './EditTpSl/MergedTpSlOrdersOverlay';

export const TpSlEditButton = ({
  genericTradeParams,
  type,
  pairMarketPrice: propPairMarketPrice,
  collateralMarketPrice: propCollateralMarketPrice,
  activeDecreaseOrders,
  children,
}: Omit<Dialog.RootProps, 'type'> & {
  pairMarketPrice?: BigNumber;
  collateralMarketPrice?: BigNumber;
  type: EditTpSlType;
  activeDecreaseOrders?: DecreaseOrderRow[];
  children?: React.ReactNode;
} & GenericTradeParams) => {
  const collateralMarketPrice = usePayTokenPriceLatest(
    genericTradeParams.collateralToken,
  );
  const pairMarketPrice = usePriceLatest(genericTradeParams.pair.baseSymbol);

  // do not show the edit button if price not fetching
  if (isNil(collateralMarketPrice) || isNil(pairMarketPrice)) return null;

  const trigger = children ?? (
    <Button
      variant="ghost"
      p={0}
      w="25px"
      h="25px"
      data-cy="EditTpSlButton"
    >
      <Icon boxSize="12px" color="greyWhite.lightGrey2">
        <Edit />
      </Icon>
    </Button>
  );

  if (type === EditTpSlType.POSITION && genericTradeParams.isMergedPosition) {
    return (
      <MergedTpSlOrdersOverlay
        genericTradeParams={genericTradeParams}
        activeDecreaseOrders={activeDecreaseOrders}
        collateralMarketPrice={collateralMarketPrice}
        pairMarketPrice={pairMarketPrice}
      >
        {trigger}
      </MergedTpSlOrdersOverlay>
    );
  }

  return (
    <EditTpSlOverlay
      key={`${genericTradeParams.takeProfit.toString()}-${genericTradeParams.stopLoss.toString()}-${activeDecreaseOrders?.map((order) => order.orderHash).join('-')}`}
      genericTradeParams={genericTradeParams}
      activeDecreaseOrders={activeDecreaseOrders}
      collateralMarketPrice={collateralMarketPrice}
      pairMarketPrice={pairMarketPrice}
      type={type}
    >
      {trigger}
    </EditTpSlOverlay>
  );
};
